import type { Paginated, PblLawyerProfile, PblVettingStatus } from '@/types/pbl';
import { formatPblDisplayDate } from '@/utils/pblDate';

export type PblVettingFilterStatus = 'all' | PblVettingStatus;

export const excludeHiddenPblVettingApplications = (applications: PblLawyerProfile[]) =>
  applications.filter((application) => application.directory_entry?.is_hidden !== true);

export const buildPblVettingApplicationParams = ({
  status,
  page,
  perPage,
}: {
  status: PblVettingFilterStatus;
  page: number;
  perPage: number;
}) => ({
  ...(status === 'all' ? {} : { status }),
  page,
  per_page: perPage,
});

export const getPblVettingProfileReference = (application: PblLawyerProfile) =>
  application.application_reference || application.reference_id || `#${application.id}`;

export const getPblVettingProfileStatus = (application: PblLawyerProfile): PblVettingStatus =>
  application.vetting_status || application.status || 'pending_review';

export const getPblVettingProfileCreatedAt = (application: PblLawyerProfile) =>
  formatPblDisplayDate(application.submitted_at || application.created_at || application.updated_at);

export const filterPblVettingApplicationsByKeyword = (
  applications: PblLawyerProfile[],
  keyword: string
) => {
  const normalizedKeyword = keyword.trim().toLowerCase();

  if (!normalizedKeyword) return applications;

  return applications.filter((application) => {
    const lawyerName = `${application.full_name_en || ''} ${application.full_name_zh || ''}`.toLowerCase();
    return (
      getPblVettingProfileReference(application).toLowerCase().includes(normalizedKeyword) ||
      lawyerName.includes(normalizedKeyword)
    );
  });
};

export const getPblVettingPaginationRange = (page: Paginated<PblLawyerProfile>) => {
  if (!page.total || page.data.length === 0) return { from: 0, to: 0 };

  const from = (page.current_page - 1) * page.per_page + 1;
  const to = Math.min(page.total, from + page.data.length - 1);

  return { from, to };
};

export const getPblVettingVisiblePages = (page: Paginated<PblLawyerProfile>) => {
  const lastPage = Math.max(1, page.last_page || 1);
  const currentPage = Math.min(Math.max(1, page.current_page || 1), lastPage);
  const start = Math.max(1, Math.min(currentPage - 2, lastPage - 4));
  const end = Math.min(lastPage, start + 4);

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

export const emptyPblVettingPage = (page = 1, perPage = 10): Paginated<PblLawyerProfile> => ({
  data: [],
  current_page: page,
  per_page: perPage,
  total: 0,
  last_page: 1,
});
